import { LoginData } from 'types/accounts';
import { PropertyData } from 'types/property';
import { useState, useRef, useEffect } from 'react';
import { toggleMobileSidebar } from 'utils/uiStore';
import { useLocation, useNavigate } from 'react-router-dom';
import { useExportNotificationContext } from 'contexts/ExportNotificationContext';
import NotificationPanel from 'components/notification/NotificationPanel';
import NotificationBellProgress from 'components/notification/NotificationBellProgress';
import HelpButton from 'components/tour/HelpButton';
import propertyService from 'api/property.service';
import profileService from 'api/profile.service';
import Icon from '../AppIcon';
import Button from './Button';
import Tooltip from './Tooltip';
import ComboBoxSearch from './ComboBoxSearch';
import { logActivity, ACTIVITY_ACTIONS, ENTITY_TYPES } from 'api/activityLogger';

const Header = () => {
  const location = useLocation();
  const navigate = useNavigate();
  const [isUserMenuOpen, setIsUserMenuOpen] = useState(false);
  const [isNotificationPanelOpen, setIsNotificationPanelOpen] = useState(false);

  // Force close notification panel on mount to prevent auto-open issues
  useEffect(() => {
    setIsNotificationPanelOpen(false);
  }, []);

  const [userData, setUserData] = useState<LoginData | null>(null);
  const [properties, setProperties] = useState<PropertyData[]>([]);
  const [selectedPropertyId, setSelectedPropertyId] = useState(() => {
    return localStorage.getItem('selectedPropertyId') || '';
  });
  const [isPropertiesLoading, setIsPropertiesLoading] = useState(true);
  const [propertiesError, setPropertiesError] = useState<string | null>(null);

  const {
    unreadCount,
    isProcessing,
    currentProgress,
    isLoading: isExportsLoading,
    handleMarkAsSeen,
  } = useExportNotificationContext();

  // Get initial dark mode preference (read-only since theme toggle is disabled)
  const isDarkMode = (() => {
    const savedTheme = localStorage.getItem('theme');
    return savedTheme === 'dark' || (!savedTheme && window.matchMedia('(prefers-color-scheme: dark)').matches);
  })();

  const propertyDropdownRef = useRef<HTMLDivElement | null>(null);
  const userMenuRef = useRef<HTMLDivElement | null>(null);
  const debounceRef = useRef<NodeJS.Timeout | null>(null);

  // Load user data from localStorage and refresh from API
  useEffect(() => {
    // Initial load from localStorage
    const userDataString = localStorage.getItem('user');
    if (userDataString) {
      try {
        const parsedUserData = JSON.parse(userDataString) as LoginData;
        setUserData(parsedUserData);
      } catch {
        // If there's an error parsing the user data, don't set it
      }
    }

    // Refresh user data from API to ensure it's up to date
    const refreshUserProfile = async () => {
      try {
        if (!localStorage.getItem('auth_token')) return;

        const response = await profileService.getMyProfile();
        if (response && response.data && response.data.user) {
          // Update state
          // We need to cast or transform if the types strictly mismatch,
          // but assuming MyProfileResponse['user'] is compatible with LoginData for shared fields
          // MyProfileResponse is slightly different, let's merge carefully
          const profileUser = response.data.user;

          setUserData(prev => {
            // We need to manually construct the LoginData shape if it's missing fields,
            // or just merge what we have. LoginData has 'organization' which profile might not have fully?
            // profile.service.ts -> MyProfileData doesn't have 'organization' object!
            // Wait, LoginData has organization object. MyProfileData does NOT.
            // If we overwrite local storage with MyProfileData, we might lose organization info!

            // We should only update the fields present in profileUser (first_name, last_name, role, etc)

            const newUser = {
              ...prev,
              first_name: profileUser.first_name,
              last_name: profileUser.last_name,
              role: profileUser.role,
              email: profileUser.email,
              // Keep existing unknown fields
            } as LoginData;

            // Update localStorage with fresh data
            localStorage.setItem('user', JSON.stringify(newUser));
            return newUser;
          });
        }
      } catch (error) {
        console.error('Failed to refresh user profile', error);
      }
    };

    refreshUserProfile();
  }, []);

  // English: Fetch properties data (only if user not super_admin)
  useEffect(() => {
    const fetchProperties = async () => {
      if (!userData) return;

      // Check if user is super_admin
      if (userData.role === 'super_admin') {
        setIsPropertiesLoading(false);
        return;
      }

      setIsPropertiesLoading(true);
      setPropertiesError(null);
      try {
        const response = await propertyService.getMyProperties(userData.organization_id as string);
        const propertyList = response.data.properties;
        setProperties(propertyList);

        // English: Default select first property if none selected
        if (propertyList.length > 0 && !selectedPropertyId) {
          setSelectedPropertyId(propertyList[0].id);
          localStorage.setItem('selectedPropertyId', propertyList[0].id);
          localStorage.setItem('selectedPropertyName', propertyList[0].name);

          // Dispatch custom event for initial property selection
          // No debounce needed for initial load
          window.dispatchEvent(
            new CustomEvent('propertyChanged', {
              detail: { propertyId: propertyList[0].id, propertyName: propertyList[0].name },
            })
          );
        }
      } catch {
        setPropertiesError('Failed to load properties. Please try again later.');
      } finally {
        setIsPropertiesLoading(false);
      }
    };

    fetchProperties();
  }, [userData, selectedPropertyId]);

  useEffect(() => {
    const handlePropertyChange = (event: CustomEvent) => {
      const propertyId = event.detail?.propertyId || '';
      const propertyName = event.detail?.propertyName || '';

      setSelectedPropertyId(propertyId);

      if (propertyId) {
        localStorage.setItem('selectedPropertyId', propertyId);
        if (propertyName) {
          localStorage.setItem('selectedPropertyName', propertyName);
        }
      } else {
        localStorage.removeItem('selectedPropertyId');
        localStorage.removeItem('selectedPropertyName');
      }
    };

    window.addEventListener('propertyChanged', handlePropertyChange as EventListener);
    return () => window.removeEventListener('propertyChanged', handlePropertyChange as EventListener);
  }, []);

  // Apply theme to document
  useEffect(() => {
    if (isDarkMode) {
      document.documentElement.classList.add('dark');
    } else {
      document.documentElement.classList.remove('dark');
    }
  }, [isDarkMode]);

  useEffect(() => {
    const handleClickOutside = (event: MouseEvent) => {
      if (userMenuRef.current && !userMenuRef.current.contains(event.target as Node)) {
        setIsUserMenuOpen(false);
      }
    };

    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, []);

  // English: Handle property selection with debounce
  const handlePropertySelect = (propertyId: string) => {
    const selected = properties.find(p => p.id === propertyId);
    if (selected) {
      // Update UI immediately
      setSelectedPropertyId(propertyId);
      localStorage.setItem('selectedPropertyId', propertyId);
      localStorage.setItem('selectedPropertyName', selected.name);

      // Log activity
      logActivity(
        ACTIVITY_ACTIONS.PROPERTY_SELECTED,
        ENTITY_TYPES.PROPERTY,
        propertyId,
        {
          property_name: selected.name,
          selected_from: 'header',
        }
      );

      // Clear previous timeout if exists
      if (debounceRef.current) {
        clearTimeout(debounceRef.current);
      }

      // Set new timeout to dispatch event
      debounceRef.current = setTimeout(() => {
        // Dispatch custom event to notify other components in the same tab
        window.dispatchEvent(
          new CustomEvent('propertyChanged', {
            detail: { propertyId, propertyName: selected.name },
          })
        );
      }, 800); // 800ms delay to prevent API spam
    }
  };

  const handleLogout = () => {
    // Clear authentication data from localStorage
    localStorage.removeItem('auth_token');
    localStorage.removeItem('user');
    localStorage.removeItem('selectedPropertyId');
    localStorage.removeItem('selectedPropertyName');

    // Clear all AI Analysis caches
    const keysToRemove: string[] = [];
    for (let i = 0; i < localStorage.length; i++) {
      const key = localStorage.key(i);
      if (
        key &&
        (key.startsWith('ai_analysis_') ||
          key.startsWith('kpi_forecast_') ||
          key.startsWith('prophet_forecast_') ||
          key.startsWith('room_price_forecast_'))
      ) {
        keysToRemove.push(key);
      }
    }
    keysToRemove.forEach(key => localStorage.removeItem(key));

    // Redirect to login page
    navigate('/login');
  };

  // Check if user is super_admin
  const isSuperAdmin = userData?.role === 'super_admin';

  // Determine badge count: show unread when not processing, hide when processing
  const badgeCount = !isProcessing ? unreadCount : 0;

  if (location.pathname === '/login') {
    return null;
  }

  return (
    <header className="fixed top-0 left-0 right-0 bg-surface border-b border-border z-50">
      <div className="flex items-center justify-between h-16 px-3 sm:px-4 flex-nowrap">
        {/* Logo */}
        <div className="flex items-center space-x-6 min-w-0">
          <div className="flex items-center space-x-3">
            <div className="w-8 h-8 flex items-center justify-center">
              <img src="/assets/images/logo.png" alt="Forecast Inn Logo" className="w-8 h-8 object-contain" />
            </div>
            <span className="hidden sm:inline text-xl font-semibold text-primary font-heading truncate max-w-[120px] xs:max-w-[140px] sm:max-w-none">
              Forecast Inn
            </span>
          </div>
        </div>

        {/* Right Section */}
        <div className="flex items-center space-x-2 md:space-x-4 pr-1 md:pr-4 flex-shrink-0">
          {/* Property Selector - Only show if not super_admin */}
          {!isSuperAdmin && (
            <div className="relative" ref={propertyDropdownRef}>
              {isPropertiesLoading ? (
                <div className="flex items-center space-x-2 px-3 py-2">
                  <Icon name="Loader" size={16} className="animate-spin" />
                  <span className="text-sm text-text-secondary hidden sm:inline">Loading properties...</span>
                </div>
              ) : propertiesError ? (
                <div className="flex items-center space-x-2 px-3 py-2">
                  <Icon name="AlertTriangle" size={16} className="text-error" />
                  <span className="text-sm text-error hidden sm:inline">{propertiesError}</span>
                </div>
              ) : (
                <div>
                  <ComboBoxSearch
                    options={properties.map(p => ({ value: p.id, label: p.name }))}
                    value={selectedPropertyId}
                    onChange={handlePropertySelect}
                    placeholder="Select a property"
                    searchPlaceholder="Search properties..."
                    className="w-40 sm:w-64 h-8"
                  />
                </div>
              )}
            </div>
          )}

          {/* Tour Help Button - only for non-superadmin */}
          {!isSuperAdmin && <HelpButton />}

          {/* Notifications */}
          <Tooltip content="Export History" position="bottom">
            <NotificationBellProgress
              progress={currentProgress}
              badgeCount={badgeCount}
              isLoading={isExportsLoading}
              onClick={() => setIsNotificationPanelOpen(true)}
            />
          </Tooltip>

          {/* User Menu */}
          <div className="relative" ref={userMenuRef}>
            <Button
              variant="ghost"
              onClick={() => setIsUserMenuOpen(!isUserMenuOpen)}
              className="flex items-center space-x-2 p-2"
            >
              <div className="w-8 h-8 bg-primary rounded-full flex items-center justify-center">
                <Icon name="User" size={16} color="white" />
              </div>
              <Icon name="ChevronDown" size={16} className="hidden sm:block" />
            </Button>

            {isUserMenuOpen && (
              <div className="absolute right-0 top-full mt-1 dropdown-menu z-1010">
                <div className="px-4 py-3 border-b border-border">
                  <p className="font-medium text-text-primary">
                    {userData ? `${userData.first_name} ${userData.last_name}` : 'User'}
                  </p>
                  <p className="text-sm text-text-secondary">
                    {userData
                      ? userData.role
                          .split('_')
                          .map(word => word.charAt(0).toUpperCase() + word.slice(1))
                          .join(' ')
                      : 'Guest'}
                  </p>
                </div>
                <div
                  onClick={() => {
                    setIsUserMenuOpen(false);
                    navigate('/profile');
                  }}
                  className="dropdown-item flex items-center space-x-2"
                >
                  <Icon name="User" size={16} />
                  <span>Profile</span>
                </div>
                <div
                  onClick={() => {
                    setIsUserMenuOpen(false);
                    navigate(`${userData?.role === 'super_admin' ? '/organization-admin' : '/organization'}`);
                  }}
                  className="dropdown-item flex items-center space-x-2"
                >
                  <Icon name="Building" size={16} />
                  <span>Organization</span>
                </div>
                <div className="border-t border-border mt-1 pt-1">
                  <div onClick={handleLogout} className="dropdown-item flex items-center space-x-2 text-error">
                    <Icon name="LogOut" size={16} />
                    <span>Logout</span>
                  </div>
                </div>
              </div>
            )}
          </div>

          {/* Mobile Menu Toggle */}
          <Button
            variant="ghost"
            className="md:hidden p-2 ml-1"
            aria-label="Toggle navigation menu"
            onClick={toggleMobileSidebar}
          >
            <Icon name="Menu" size={20} />
          </Button>
        </div>
      </div>

      <NotificationPanel
        isOpen={isNotificationPanelOpen}
        onClose={() => setIsNotificationPanelOpen(false)}
        onMarkAsSeen={handleMarkAsSeen}
      />
    </header>
  );
};

export default Header;
